<template>
  <base-draggable-modal
      :name="modal_name"
      max-height="80vh"
      width="50vw"
      :fullscreen="$vuetify.display.xsOnly"
      :ref="modal_name">
    <v-card rounded="xl">
      <base-modal>
        <template #header>
          <v-toolbar class="draggable_selector" flat color="secondary" density="compact">
            <v-toolbar-title v-if="label" class="text-primary">{{ label }}</v-toolbar-title>
            <v-toolbar-title v-else class="text-primary">{{ $store.getters.translate(page.model) }}</v-toolbar-title>
            <v-spacer/>
            <base-action-button v-if="canDelete" class="mr-5" :delete="true" @action="archive">
              <template #prepend><PhArchive style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
              {{ $store.getters.translate("delete") }}
            </base-action-button>
          </v-toolbar>
        </template>
        <template #content>
            <v-card-text>
              <v-row v-if="logo_required && logo">
                <v-col>
                  <v-layout align-center justify-center column fill-height>
                    <v-img max-width="150" max-height="150" :src="logo" class="ma-auto"/>
                  </v-layout>
                </v-col>
              </v-row>
              <!--USUAL FIELDS-->
              <base-field
                  v-for="field in usual_fields"
                  :key="field.name"
                  :isDisabled="!canEdit"
                  :modelValue="record[field.name]"
                  :field="field"
                  @change="usualFieldDataChanged"/>
              <!--CUSTOM FIELDS-->
              <base-field
                  v-if="record.custom_fields"
                  v-for="field in custom_fields"
                  :key="field.name"
                  :isDisabled="!canEdit"
                  :modelValue="record.custom_fields[field.name]"
                  :field="field"
                  @change="customFieldDataChanged"/>
              <!--LIST FILES-->
              <v-row v-if="files_required" class="mt-5">
                <v-col>
                  <base-list-files
                      :page="page"
                      :parent_id="record.id"
                      :label="$store.getters.translate('files')"
                      :files="record.files"
                      :multiple_files="true"
                      :deletable="canEdit"
                      :create="canEdit"
                      @refresh="load(record.id)"/>
                </v-col>
              </v-row>
              <!--TAGS-->
              <base-tags v-if="tags_required" :isDisabled="!canEdit" :available_tags="available_tags" :modelValue="record.tags" :modal_name="page.name" @change="tagsChanged"/>
            </v-card-text>
        </template>
        <template #footer>
          <v-card-text>
            <v-row>
              <v-col>
                <base-action-button :width="'100%'" @action="closeModal(modal_name)">
                  <template #prepend><PhX style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
                  {{ $store.getters.translate("cancel") }}
                </base-action-button>
              </v-col>
              <v-col v-if="canEdit">
                <base-action-button :background="true" :width="'100%'" @action="save(true)">
                  <template #prepend><PhFloppyDisk style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
                  {{ $store.getters.translate("save") }}
                </base-action-button>
              </v-col>
            </v-row>
          </v-card-text>
        </template>
      </base-modal>
    </v-card>
  </base-draggable-modal>
</template>

<script>
import { useAbility } from '@casl/vue';
import helpFunctions from "../../plugins/helpFunctions";
import BaseModal from "../../components/commonComponents/BaseModal";
import BaseDraggableModal from "../../components/commonComponents/BaseDraggableModal";
import BaseTags from "./BaseTags";
import BaseListFiles from "./BaseListFiles";
import BaseActionButton from "./BaseActionButton";
import BaseLinkButton from "./BaseLinkButton";
import {PhArchive, PhArrowSquareOut, PhFloppyDisk, PhX} from "@phosphor-icons/vue";

export default {
  props: ["modal_name", "page", "label", "files_required", "multiple_files", "available_tags", "logo_required", "internal_model", "preview", "custom_permission_can_edit", "custom_permission_can_delete"],
  components: {
    BaseLinkButton,
    BaseModal,
    BaseDraggableModal,
    BaseTags,
    BaseListFiles,
    BaseActionButton,
    PhArrowSquareOut, PhArchive, PhFloppyDisk, PhX,
  },
  setup() {
    const { can } = new useAbility();
    return { can }
  },
  data() {
    return {
      record: {},
      usual_fields: [],
      custom_fields: [],
      tags_required: false,
      logo: null
    };
  },
  methods: {
    ...helpFunctions.modal_functions,
    async load(id) {
      this.usual_fields = [];
      this.custom_fields = [];
      this.logo = null;
      this.openModal(this.modal_name);
      this.record = await helpFunctions.loadModel(this.$toast, this.page.name, id);
      await this.getFields();
      this.checkLogo();
      if(this.page.model === 'tag') {
        this.record.name = this.record.name.en;
      }
    },
    async save(reload) {
      if (helpFunctions.modelFieldsFilled(this.$toast, this.record, this.usual_fields)) {
        if(await helpFunctions.saveModel(this.$toast, this.page.name, this.record)) {
          if(this.page.model === 'translation') {
            await this.$store.dispatch("refreshTranslations");
          }
          if (reload) {
            this.$emit('refresh', this.record);
            this.closeModal(this.modal_name);
          }
        }
      }
    },
    async archive() {
      this.$confirm(this.$store.getters.translate("delete_confirmation")).then(async (res) => {
        if (res) {
          await helpFunctions.deleteModel(this.$toast, this.page.name, this.record.id);
          this.$emit('refresh');
          this.closeModal(this.modal_name);
        }
      });
    },
    checkLogo() {
      if(this.logo_required && this.record.files) {
        for (let i = 0; i < this.record.files.length; i++) {
          if (this.record.files[i].content_type === "image/png" || this.record.files[i].content_type === "image/jpeg") {
            this.logo = this.record.files[i].full_path;
            break;
          }
        }
      }
    },
    usualFieldDataChanged(data) {
      if(this.record[data[0]] !== data[1]) {
        this.record[data[0]] = data[1];
      }
    },
    customFieldDataChanged(data) {
      this.record.custom_fields[data[0]] = data[1];
    },
    tagsChanged(data) {
      this.record.tags = data;
    },
    async getFields() {
      const response = await helpFunctions.getFields(this.page.model, "show_on_update");
      this.tags_required = response.tags_required;
      this.usual_fields = response.usual_fields;
      this.custom_fields = response.custom_fields;
    },
  },
  computed: {
    canEdit() {
      return !this.preview && (this.internal_model || this.can("edit", this.$createEntity(this.page.model.toLowerCase(), { id: 0 })) ||
          this.can("edit", this.$createEntity(this.page.model.toLowerCase(), { id: this.record.id })) || this.custom_permission_can_edit);
    },
    canDelete() {
      return !this.preview && (this.internal_model || this.can("delete", this.$createEntity(this.page.model.toLowerCase(), { id: 0 })) ||
          this.can("delete", this.$createEntity(this.page.model.toLowerCase(), { id: this.record.id })) || this.custom_permission_can_delete);
    },
  },
};
</script>

<style scoped>
.article-img {
  max-height: 80px;
  max-width: 80px;
}
</style>